import { Permissions, TeamRole, UserProfileRole, Workspace } from "ts-interfaces";

/**
 * Derive a user's role within a given firm from their permissions document.
 *
 * Role membership is encoded redundantly:
 *   - in teamsAdmin     only        → admin
 *   - in teamsDesigner  only        → designer
 *   - in both arrays                → designer_and_admin
 *   - in neither                    → null (not a member)
 */
export const deriveTeamRole = (
  permissions: Permissions | null | undefined,
  firmId: string | null | undefined,
): TeamRole | null => {
  if (!permissions || !firmId) return null;
  const isAdmin = permissions.teamsAdmin?.includes(firmId) ?? false;
  const isDesigner = permissions.teamsDesigner?.includes(firmId) ?? false;
  if (isAdmin && isDesigner) return TeamRole.DESIGNER_AND_ADMIN;
  if (isAdmin) return TeamRole.ADMIN;
  if (isDesigner) return TeamRole.DESIGNER;
  return null;
};

export const isAdminRole = (role: TeamRole | null): boolean =>
  role === TeamRole.ADMIN || role === TeamRole.DESIGNER_AND_ADMIN;

export const isDesignerRole = (role: TeamRole | null): boolean =>
  role === TeamRole.DESIGNER || role === TeamRole.DESIGNER_AND_ADMIN;

/**
 * Resolve a user's effective Pro entitlement.
 *
 * The single rule for the whole app: a user is Pro iff they hold a personal
 * Pro subscription OR a *designer* seat in an active team. A pure `admin`
 * (admin without a designer seat) is managerial only and is NOT Pro — this is
 * why we recompute on every role change rather than letting a once-stamped
 * `userRole === "pro"` flag stick after a Designer & Admin → Admin demotion.
 *
 * Pure function so it can be unit-tested without Firestore; callers supply
 * the two signals.
 */
export const resolveUserRole = ({
  hasPersonalSub,
  hasDesignerSeat,
}: {
  /** The user holds a personal Stripe subscription (profile.stripeSubscriptionId). */
  hasPersonalSub: boolean;
  /** The user is a designer (or designer_and_admin) in at least one active team. */
  hasDesignerSeat: boolean;
}): UserProfileRole =>
  hasPersonalSub || hasDesignerSeat
    ? UserProfileRole.PRO
    : UserProfileRole.BASIC;

/**
 * Resolve whether a user is Pro **inside a specific workspace** — the unit of
 * Pro entitlement once Pro is decoupled from a single global `profile.userRole`
 * flag (see docs/workspaces-architecture.md).
 *
 * The `Workspace` argument carries its own identity: `.kind` selects the branch
 * and, for teams, `.firmId` is the firm the role is derived against. This makes
 * the function explicitly per-firm, so it can evaluate ANY of the user's
 * workspaces (not just the active one) — the caller resolves the per-firm
 * signals (`isActiveRosterMember`, `teamSubActive`) for `workspace.firmId` and
 * calls once per workspace. That is what makes "inactive in team A, active in
 * team B" representable.
 *
 *   - Personal workspace → Pro iff the user holds a personal entitlement
 *     (`isPersonalPro` = `profile.userRole === "pro"`, which after the writer
 *     cleanup covers a personal Stripe sub OR an admin-assigned pro). A team
 *     seat does NOT make the personal workspace Pro.
 *   - Team workspace → Pro iff the user holds a *designer* seat in THAT firm,
 *     is an active roster member of it, AND the firm's subscription is live.
 *     A pure `admin` (no designer seat) is managerial only and is NOT Pro.
 *
 * Pure function (no Firestore / hook reads) so it is unit-testable; the hook
 * resolves the per-firm signals.
 */
export const resolveWorkspacePro = ({
  workspace,
  isPersonalPro,
  permissions,
  isActiveRosterMember,
  teamSubActive,
}: {
  /** The workspace to evaluate — carries `.kind` and (teams) `.firmId`. */
  workspace: Workspace;
  /** Personal entitlement: `profile.userRole === UserProfileRole.PRO`. */
  isPersonalPro: boolean;
  /** The user's permissions doc, to derive their role in `workspace.firmId`. */
  permissions: Permissions | null | undefined;
  /** Is the user an active (non-inactive) roster member of `workspace.firmId`? */
  isActiveRosterMember: boolean;
  /** Is the firm's plan live? `isTeamPlanActive(firm)`. */
  teamSubActive: boolean;
}): boolean => {
  if (workspace.kind === "personal") return isPersonalPro;
  const role = deriveTeamRole(permissions, workspace.firmId);
  return isDesignerRole(role) && isActiveRosterMember && teamSubActive;
};

// status is a plain string: the cancel webhook writes "cancelled", outside DesignFirmStatus.
type FirmPlanFields =
  | { stripeSubscriptionId?: string | null; contractTeam?: boolean | null; status?: string | null }
  | null
  | undefined;

/** A staff-set contract team on an active firm (not lapsed "cancelled", not archived). */
export const isContractTeam = (firm: FirmPlanFields): boolean =>
  firm?.contractTeam === true && (!firm.status || firm.status === "active");

/** Live = a Stripe subscription, or a contract team. */
export const isTeamPlanActive = (firm: FirmPlanFields): boolean =>
  Boolean(firm?.stripeSubscriptionId) || isContractTeam(firm);

/**
 * All firm IDs a user belongs to, deduped, preserving teamsAdmin ordering first.
 */
export const allFirmIdsForUser = (
  permissions: Permissions | null | undefined,
): string[] => {
  if (!permissions) return [];
  const admin = permissions.teamsAdmin ?? [];
  const designer = permissions.teamsDesigner ?? [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of [...admin, ...designer]) {
    if (!seen.has(id)) {
      seen.add(id);
      out.push(id);
    }
  }
  return out;
};
